import React from "react";
import Axios from "axios";
import {Link, Redirect} from "react-router-dom";
import BlockUi from "react-block-ui";
import Image from "components/common/Image";
import logoImage2 from "assets/img/sxp-logo-black-2.png";
import { API } from "api/APIClient";
import AppComponent from "components/common/AppComponent";
import PasswordNotCompliant from "./PasswordNotCompliant";

class TwoFactor extends AppComponent {

    timerInterval = null;
    inputRefs = [];

    state = {
        codeDigits: ["", "", "", "", "", ""],
        errors: {},
        isUploading: false,
        isResending: false,
        now: Date.now(),
        showNoCompliant: false
    };

    componentDidMount() {
        this.timerInterval = setInterval(() => {
            this.setState({
                now: Date.now()
            });
        }, 1000);
    }

    componentWillUnmount() {
        clearInterval(this.timerInterval);
    }

    getRemaining = (targetMs) => {
        return Math.max(0, Math.ceil((targetMs - this.state.now) / 1000));
    }

    handleSubmit = (event) => {
        event.preventDefault();

        if (this.state.isUploading) {
            return;
        }

        const twoFactorData = this.context.twoFactorData;
        if (!twoFactorData || !twoFactorData.email) {
            this.context.clearPendingTwoFactor();
            return;
        }

        const code = this.state.codeDigits.join("");
        if (code.length !== 6) {
            this.setState({
                errors: {
                    code: "Please enter the 6-digit verification code."
                }
            });
            return;
        }

        this.setState({
            isUploading: true,
            errors: {}
        }, () => {
            Axios
                .post(API.auth.verifyTwoFactor, {
                    email: twoFactorData.email,
                    code: code
                })
                .then((res) => {
                    if (res.data.success === true) {
                        if (res.data.data.needsNewPassword) {
                            this.context.clearPendingTwoFactor();
                            this.setState({
                                showNoCompliant: true,
                                isUploading: false
                            });
                        } else {
                            this.context.login(res.data.data);
                        }
                    } else {
                        this.setState({
                            errors: {
                                genericError: "Invalid or expired authentication code."
                            },
                            isUploading: false
                        });
                    }
                })
                .catch(() => {
                    this.setState({
                        errors: {
                            genericError: "Authentication failed. Please try again."
                        },
                        isUploading: false
                    });
                });
        });
    }

    handleResend = () => {
        const twoFactorData = this.context.twoFactorData;
        if (this.state.isResending || !twoFactorData || !twoFactorData.email) {
            return;
        }

        this.setState({
            isResending: true,
            errors: {}
        }, () => {
            Axios
                .post(API.auth.resendTwoFactor, {
                    email: twoFactorData.email
                })
                .then((res) => {
                    if (res.data.success === true && res.data.data) {
                        this.context.setPendingTwoFactor({
                            email: twoFactorData.email,
                            expiresInSeconds: res.data.data.expiresInSeconds,
                            resendAvailableInSeconds: res.data.data.resendAvailableInSeconds
                        });
                    } else {
                        this.setState({
                            errors: {
                                genericError: "Unable to resend code. Please try again."
                            }
                        });
                    }

                    this.setState({
                        isResending: false
                    });
                })
                .catch((error) => {
                    const isRateLimited = error && error.response && error.response.status === 429;
                    this.setState({
                        errors: {
                            genericError: isRateLimited
                                ? "Too many resend requests. Please wait and try again."
                                : "Unable to resend code. Please try again."
                        },
                        isResending: false
                    });
                });
        });
    }

    onCodeChange = (index, event) => {
        const raw = event.target.value || "";
        const digits = raw.replace(/\D/g, "");

        if (!digits.length) {
            const codeDigits = [...this.state.codeDigits];
            codeDigits[index] = "";
            this.setState({
                codeDigits,
                errors: {}
            });
            return;
        }

        const codeDigits = [...this.state.codeDigits];
        let nextIndex = index;
        for (let i = 0; i < digits.length && nextIndex < 6; i += 1) {
            codeDigits[nextIndex] = digits[i];
            nextIndex += 1;
        }

        this.setState({
            codeDigits,
            errors: {}
        }, () => {
            if (nextIndex <= 5 && this.inputRefs[nextIndex]) {
                this.inputRefs[nextIndex].focus();
            }
        });
    }

    onCodeKeyDown = (index, event) => {
        if (event.key === "Backspace") {
            if (this.state.codeDigits[index]) {
                const codeDigits = [...this.state.codeDigits];
                codeDigits[index] = "";
                this.setState({
                    codeDigits,
                    errors: {}
                });
            } else if (index > 0 && this.inputRefs[index - 1]) {
                this.inputRefs[index - 1].focus();
            }
        }
    }

    onCodePaste = (index, event) => {
        event.preventDefault();
        const pasted = (event.clipboardData.getData("text") || "").replace(/\D/g, "");
        if (!pasted.length) {
            return;
        }

        const codeDigits = [...this.state.codeDigits];
        let nextIndex = index;
        for (let i = 0; i < pasted.length && nextIndex < 6; i += 1) {
            codeDigits[nextIndex] = pasted[i];
            nextIndex += 1;
        }

        this.setState({
            codeDigits,
            errors: {}
        }, () => {
            const focusIndex = Math.min(nextIndex, 5);
            if (this.inputRefs[focusIndex]) {
                this.inputRefs[focusIndex].focus();
            }
        });
    }

    render() {
        if (this.context.user !== null) {
            return <Redirect to="/" />;
        }

        if (this.state.showNoCompliant && this.context.twoFactorData) {
            return <PasswordNotCompliant email={this.context.twoFactorData.email} />;
        }

        if (!this.context.twoFactorPending || !this.context.twoFactorData) {
            return <Redirect to="/login" />;
        }

        const twoFactorData = this.context.twoFactorData;
        const expiresIn = this.getRemaining(twoFactorData.expiresAt);
        const resendIn = this.getRemaining(twoFactorData.resendAvailableAt);
        const isExpired = expiresIn === 0;

        return (
            <BlockUi tag="div" blocking={this.state.isUploading || this.state.isResending}>
                <div className="login-wrapper">
                    <div className="login-box">
                        <div className="grid-container">
                            <div className="grid-x grid-padding-x">
                                <div className="cell large-2 large-offset-5 medium-6 medium-offset-3 small-10 small-offset-1">
                                    <Image src={logoImage2} alt="Uniwellbeing Logo" fit="contain"/>
                                </div>
                                <div className="cell large-4 large-offset-4 medium-8 medium-offset-2 small-12">
                                    <h1>Verification</h1>
                                    <p>We sent a verification code to <strong>{twoFactorData.email}</strong>.</p>
                                    <p>Code expires in: <strong>{expiresIn}s</strong></p>
                                </div>
                            </div>
                            <form onSubmit={this.handleSubmit}>
                                <div className="grid-x grid-padding-x">
                                    <div className={"cell large-4 large-offset-4 medium-8 medium-offset-2 small-12 " + (this.state.errors.code && "error")}>
                                        <label htmlFor="code">
                                            Verification code
                                            <div style={{display: "flex", justifyContent: "space-between", gap: 8}}>
                                                {this.state.codeDigits.map((digit, index) => (
                                                    <input
                                                        key={index}
                                                        ref={(el) => { this.inputRefs[index] = el; }}
                                                        type="text"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        maxLength={6}
                                                        value={digit}
                                                        onChange={(event) => this.onCodeChange(index, event)}
                                                        onKeyDown={(event) => this.onCodeKeyDown(index, event)}
                                                        onPaste={(event) => this.onCodePaste(index, event)}
                                                        style={{textAlign: "center"}}
                                                    />
                                                ))}
                                            </div>
                                            <small className="error">{this.state.errors.code}</small>
                                        </label>
                                    </div>
                                    <div className="cell small-12">
                                        <div className="form-error-msg grid-x grid-padding-x" style={{display: this.state.errors.genericError ? "block" : "none"}}>
                                            <div className="cell small-12">
                                                <span>{this.state.errors.genericError}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="cell large-4 large-offset-4 medium-8 medium-offset-2 small-12">
                                        <button className="button" type="submit" disabled={isExpired}>Verify code</button>
                                    </div>
                                    <div className="cell large-4 large-offset-4 medium-8 medium-offset-2 small-12">
                                        <button className="button hollow" type="button" onClick={this.handleResend} disabled={resendIn > 0 || this.state.isResending}>
                                            {resendIn > 0 ? `Resend code (${resendIn}s)` : "Resend code"}
                                        </button>
                                    </div>
                                    <div className="cell large-4 large-offset-4 medium-8 medium-offset-2 small-12">
                                        <Link to="/login" className="forgot" onClick={this.context.clearPendingTwoFactor}>Back to login</Link>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </BlockUi>
        );
    }
}

export default TwoFactor;
